// Used for setting color scale in Gauge chart

import { TextField, Tooltip } from '@mui/material';
import { useState, useEffect, useMemo, useRef } from 'react';
import { connect, useSelector } from 'react-redux';

import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import { SelectListItem } from '../../CommonFunctions/SelectListItem';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ChartColors from './ChartColors';
import './ColorSteps.css';
import { Dispatch } from 'redux';
import {
  addingNewStep,
  changingValuesofSteps,
  setManualColorGauge,
  switchAutotoManualinSteps,
  updateGaugeAxisOptions,
} from '../../../redux/ChartPoperties/ChartControlsActions';
import { ChartOptionsProps, ChartOptionsStateProps } from '../CommonInterfaceForChartOptions';
import { AlertColor } from '@mui/material/Alert';
import { handleKeyDown } from '../../CommonFunctions/CommonFunctions';
import useCombinedColorSchemes from '../../../hooks/useCombinedColorSchemes';
import { RootState } from '../../../redux';
import { OrientationRadioGroup } from '../Legend/LegendComponents/OrientationRadioGroup';
import ColorPickerPopover from './ChartColorComponents/ColorPickerPopover';

const textFieldStyleProps = {
  style: {
    fontSize: '12px',
    // backgroundColor: 'white',
    height: '10px',
    color: '#404040',
  },
};

interface ColorStepsActions {
  changingValuesofSteps: (propKey: string, value: any) => void;
  setManualColorGauge: (propKey: string, value: any) => void;
  switchAutotoManualinSteps: (propKey: string, value: any) => void;
  addingNewStep: (propKey: string, index: number, value: any) => void;
  updateGaugeAxisOptions: (propKey: string, option: string, value: any) => void;
  iconColor: string;
}

const ColorSteps = ({
  // state
  chartControls,
  tabTileProps,
  iconColor,

  // dispatch
  addingNewStep,
  changingValuesofSteps,
  updateGaugeAxisOptions,
  setManualColorGauge,
  switchAutotoManualinSteps,
}: ChartOptionsProps & ColorStepsActions) => {
  const { tabState } = useSelector((state: RootState) => ({
    tabState: state.tabState,
  }));
  var propKey = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;

  const [severity, setSeverity] = useState<AlertColor>('success');
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [testMessage, setTestMessage] = useState<string>('Testing alert');

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedStepIndex, setSelectedStepIndex] = useState<number | null>(null);

  const [colorsOfScheme, setColorsOfScheme] = useState<any>([]);

  const allThemes = useCombinedColorSchemes();

  const defaultThemeId = tabState.tabs[tabTileProps.selectedTabId]?.dashboardState?.defaultThemeId;

  const defaultThemeName = useMemo(() => {
    let theme = defaultThemeId
      ? allThemes.find((t) => t.id === defaultThemeId || t.themeId === defaultThemeId)
      : undefined;
    if (!theme && allThemes.length > 0) {
      theme = allThemes[0];
    }
    return theme?.name;
  }, [defaultThemeId, allThemes]);

  let chartData = chartControls.properties[propKey].chartData
    ? chartControls.properties[propKey].chartData
    : [];

  const isStepsAuto = chartControls.properties[propKey].axisOptions.gaugeChartControls.isStepsAuto;
  const isStepsAutoRef = useRef(isStepsAuto);

  useEffect(() => {
    isStepsAutoRef.current = isStepsAuto;
  }, [isStepsAuto]);

  useEffect(() => {
    var col: any = [];
    allThemes.forEach((el) => {
      const themeName = chartControls.properties[propKey].colorScheme
        ? chartControls.properties[propKey].colorScheme
        : defaultThemeName;
      if (el.name === themeName) {
        setColorsOfScheme(el.colors);
        col.push(...el.colors);
      }
    });

    if (isStepsAutoRef.current) {
      /* when theme change  'isColorAuto' prop of all steps set to 'ture' to show the colors of selected theme */
      const ArrayOfStepsWithSchemaColors = JSON.parse(
        JSON.stringify(chartControls.properties[propKey].axisOptions.gaugeChartControls.stepcolor),
      ).map((element: any, index: number) => {
        var id = index >= col.length ? index % col.length : index;
        element.isColorAuto = true;
        element.color = col[id];
        return element;
      });

      changingValuesofSteps(propKey, ArrayOfStepsWithSchemaColors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartControls.properties[propKey].colorScheme, defaultThemeId, defaultThemeName, allThemes]);

  useEffect(() => {
    var newTempData: any = [];
    var total: number;
    if (chartData && chartData[0]) {
      Object.keys(chartData[0]).forEach((key) => {
        newTempData.push({
          name: key,
          value: chartData[0][key],
        });
      });

      if (isStepsAutoRef.current) {
        total = newTempData[0].value * 2;
        const stepsWithValues = JSON.parse(
          JSON.stringify(
            chartControls.properties[propKey].axisOptions.gaugeChartControls.stepcolor,
          ),
        ).map((el: any) => {
          el.value = Math.ceil((el.stepValue * total) / 100);
          return el;
        });
        changingValuesofSteps(propKey, stepsWithValues);
        updateGaugeAxisOptions(propKey, 'max', total);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData]);
  /* function to remove existing steps and update the remining steps*/
  const removeStep = (index: number) => {
    setManualColorGauge(propKey, true);

    switchAutotoManualinSteps(propKey, false);
    updateGaugeAxisOptions(propKey, 'isMaxAuto', false);

    const reminingSteps = JSON.parse(
      JSON.stringify(chartControls.properties[propKey].axisOptions.gaugeChartControls.stepcolor),
    )
      .filter((el: any, i: number) => {
        return i !== index;
      })
      .map((el: any) => {
        el.isColorAuto = false;
        return el;
      });

    var total: any = getTotal(reminingSteps);
    if (total === 0) total = 1;

    var maxTotalAndUpdatedArray: any = computeAndGetMaxValue(reminingSteps, total);

    changingValuesofSteps(propKey, maxTotalAndUpdatedArray.arrayWithUpdatedValueOfNewStep);
    updateGaugeAxisOptions(propKey, 'max', maxTotalAndUpdatedArray.maxTotal);
  };
  /* changing value of existing step (edit)*/
  const changeStepValue = (value: any, index: number) => {
    switchAutotoManualinSteps(propKey, false);

    updateGaugeAxisOptions(propKey, 'isMaxAuto', false);

    let newValue = 0;
    if (value !== '' && !isNaN(Number(value))) {
      newValue = Number(value);
    }

    const stepWithChangedValue = JSON.parse(
      JSON.stringify(chartControls.properties[propKey].axisOptions.gaugeChartControls.stepcolor),
    ).map((el: any, i: number) => {
      if (index === i) {
        el.value = newValue; // Update with clean number
      }
      return el;
    });

    var total = getTotal(stepWithChangedValue);
    if (total === 0) total = 1;
    var maxTotalAndUpdatedArray: any = computeAndGetMaxValue(stepWithChangedValue, total);

    changingValuesofSteps(propKey, maxTotalAndUpdatedArray.arrayWithUpdatedValueOfNewStep);
    updateGaugeAxisOptions(propKey, 'max', maxTotalAndUpdatedArray.maxTotal);
  };

  /* adding newsteps */
  const addNewStep = (obj: any, idx: number) => {
    switchAutotoManualinSteps(propKey, false);

    updateGaugeAxisOptions(propKey, 'isMaxAuto', false);

    const newStepAddedArray = JSON.parse(
      JSON.stringify(chartControls.properties[propKey].axisOptions.gaugeChartControls.stepcolor),
    );

    newStepAddedArray.splice(idx, 0, obj);

    if (colorsOfScheme && colorsOfScheme.length > 0) {
      newStepAddedArray.forEach((step: any, i: number) => {
        if (step.isColorAuto) {
          const colorIndex = i % colorsOfScheme.length;
          step.color = colorsOfScheme[colorIndex];
        }
      });
    }

    var total = getTotal(newStepAddedArray);
    if (total === 0) total = 1; // Safety check

    var maxTotalAndUpdatedArray: any = computeAndGetMaxValue(newStepAddedArray, total);

    changingValuesofSteps(propKey, maxTotalAndUpdatedArray.arrayWithUpdatedValueOfNewStep);
    updateGaugeAxisOptions(propKey, 'max', maxTotalAndUpdatedArray.maxTotal);
  };
  /* getting total value of all steps*/
  const getTotal = (stepsArray: any) => {
    let total: number = 0;
    stepsArray.forEach((el: any) => {
      total = total + parseInt(el.value);
    });
    return total;
  };
  const computeAndGetMaxValue = (stepsArray: any, total: any) => {
    var per = 0;
    var stepValue = 0;
    var i = 0;
    var maxTotalAndUpdatedArray = {};

    for (i = 0; i < stepsArray.length; i++) {
      per = per + stepsArray[i].value / total;
      stepValue = (stepsArray[i].value * 100) / total;
      maxTotalAndUpdatedArray = getMaxTotalAndUpdatedArray(
        Number(per.toFixed(4)),
        stepValue,
        i,
        stepsArray,
      );
    }
    return maxTotalAndUpdatedArray;
  };

  const getMaxTotalAndUpdatedArray = (per: any, stepValue: any, index: number, stepsArray: any) => {
    var maxTotal = 0;
    const arrayWithUpdatedValueOfNewStep = stepsArray.map((el: any, i: number) => {
      maxTotal = maxTotal + el.value;
      if (i === index) {
        el.per = per;
        el.stepValue = stepValue;
      }
      return el;
    });

    return { maxTotal, arrayWithUpdatedValueOfNewStep };
  };
  const getbgcolor = (index: number) => {
    var idx = index;
    const currentColors = colorsOfScheme;

    if (currentColors.length === 0) {
      return '';
    }

    var colorValue = '';
    if (idx >= currentColors.length) {
      var id2 = idx % currentColors.length;
      colorValue = currentColors[id2];
      return colorValue;
    } else {
      colorValue = currentColors[idx];
      return colorValue;
    }
  };

  //   Color Handler
  const handleColorChange = (newColor: string) => {
    if (selectedStepIndex === null) return;
    switchAutotoManualinSteps(propKey, false);

    const stepsWithUserSelectedColor = chartControls.properties[
      propKey
    ].axisOptions.gaugeChartControls.stepcolor.map((element: any, index: number) => {
      if (index === selectedStepIndex) {
        element.color = newColor;
        element.isColorAuto = false;
      }
      return element;
    });
    changingValuesofSteps(propKey, stepsWithUserSelectedColor);
  };

  //   current color for the popover
  const stepsList = chartControls.properties[propKey].axisOptions.gaugeChartControls.stepcolor;
  const currentSelectedColor =
    selectedStepIndex !== null && stepsList[selectedStepIndex]
      ? stepsList[selectedStepIndex].color
      : '#FFFFFF';

  const isManualMode = chartControls.properties[propKey].axisOptions.gaugeChartControls.manualColor;

  return (
    <div style={{ marginTop: '20px', paddingTop: '8px', paddingLeft: '4px' }}>
      <OrientationRadioGroup
        orientation={isManualMode ? 'manual' : 'auto'}
        options={[
          { name: 'Auto', key: 'auto' },
          { name: 'Manual', key: 'manual' },
        ]}
        onChange={(key) => {
          setManualColorGauge(propKey, key === 'manual');
        }}
        style={{ width: '94%' }}
      />

      <div>
        {isManualMode ? (
          <>
            <div
              className="optionDescription"
              style={{
                marginTop: '0',
                paddingLeft: '0.5rem',
                color: iconColor ? iconColor : '#404040',
              }}
            >
              STEPS:
            </div>
            <div className=" colorStepsList">
              {stepsList.map((el: any, index: number) => {
                return (
                  <div key={index}>
                    <SelectListItem
                      key={index}
                      render={(xprops: any) => (
                        <div
                          onMouseOver={() => xprops.setOpen(true)}
                          onMouseLeave={() => xprops.setOpen(false)}
                        >
                          <div className="colorStepsInput">
                            <TextField
                              type="number"
                              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) =>
                                handleKeyDown(event)
                              }
                              style={{ flex: 1, marginRight: '5px' }}
                              onChange={(e) => {
                                changeStepValue(e.target.value, index);
                              }}
                              value={el.value}
                              inputProps={{
                                ...textFieldStyleProps,
                                style: {
                                  ...textFieldStyleProps.style,
                                  color: iconColor ? iconColor : '#404040',
                                },
                              }}
                              sx={{
                                color: iconColor || '#404040',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: iconColor || '#2bb9bb',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: iconColor || '#2bb9bb',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: iconColor || '#2bb9bb',
                                },
                                '& .MuiOutlinedInput-input': {
                                  padding: '8px 14px',
                                  color: iconColor || '#404040',
                                },
                                '@supports (-moz-appearance: none)': {
                                  '& .MuiOutlinedInput-input': {
                                    padding: '8px 2px 8px 8px',
                                  },
                                },
                              }}
                            />
                            <Tooltip
                              title="Click to change color"
                              slotProps={{
                                tooltip: {
                                  sx: {
                                    fontSize: '12px',
                                  },
                                },
                              }}
                            >
                              <div
                                className="colorIndicator"
                                style={{
                                  backgroundColor: el.isColorAuto ? getbgcolor(index) : el.color,
                                }}
                                onClick={(e) => {
                                  setSelectedStepIndex(index);
                                  setAnchorEl(e.currentTarget);
                                }}
                              ></div>
                            </Tooltip>
                            <div>
                              <div className="colorStepsAddDelete">
                                {xprops.open ? (
                                  <>
                                    <div
                                      style={{
                                        cursor: 'pointer',
                                        justifyContent: 'center',
                                      }}
                                      onClick={(e) => {
                                        var idx = index + 1;
                                        var colorValue = getbgcolor(idx);
                                        var defaultValue = el.value > 0 ? el.value : 100;
                                        var obj = {
                                          stepValue: 1,
                                          color: colorValue,
                                          per: el.per,
                                          isColorAuto: true,
                                          value: defaultValue,
                                        };

                                        addNewStep(obj, idx);
                                      }}
                                    >
                                      <Tooltip
                                        title="Add Below"
                                        slotProps={{
                                          tooltip: {
                                            sx: {
                                              fontSize: '12px',
                                            },
                                          },
                                        }}
                                      >
                                        <AddIcon
                                          sx={{
                                            color: iconColor || '#666',
                                            height: '23px',
                                            width: '23px',
                                            padding: '1px',
                                            marginRight: '4px',
                                            '&:hover': {
                                              //  backgroundColor: '#d7d9db',
                                              borderRadius: '2px',
                                            },
                                          }}
                                        />
                                      </Tooltip>
                                    </div>
                                    <div
                                      style={{
                                        cursor: 'pointer',
                                        justifyContent: 'center',
                                      }}
                                      onClick={() => {
                                        /* deleting step */
                                        if (
                                          chartControls.properties[propKey].axisOptions
                                            .gaugeChartControls.stepcolor.length === 1
                                        ) {
                                          setOpenAlert(true);
                                          setSeverity('warning');
                                          setTestMessage('atleast one step should be there');
                                          setTimeout(() => {
                                            setOpenAlert(false);
                                            setTestMessage('');
                                          }, 3000);
                                        } else {
                                          removeStep(index);
                                        }
                                      }}
                                    >
                                      <Tooltip
                                        title="Delete"
                                        slotProps={{
                                          tooltip: {
                                            sx: {
                                              fontSize: '12px',
                                            },
                                          },
                                        }}
                                      >
                                        <DeleteIcon
                                          sx={{
                                            color: iconColor || '#666',
                                            height: '23px',
                                            width: '23px',
                                            padding: '2px',
                                            '&:hover': {
                                              color: 'red',
                                              // backgroundColor: '#d7d9db',
                                              borderRadius: '2px',
                                            },
                                          }}
                                        />
                                      </Tooltip>
                                    </div>
                                  </>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    />
                  </div>
                );
              })}
            </div>

            <NotificationDialog
              severity={severity}
              openAlert={openAlert}
              testMessage={testMessage}
              onCloseAlert={() => {
                setOpenAlert(false);
                setTestMessage('');
              }}
            />

            <ColorPickerPopover
              isOpen={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              currentColor={currentSelectedColor}
              onColorChange={handleColorChange}
              propKey={propKey}
              selectedMenu=""
            />
          </>
        ) : (
          <div style={{ marginTop: '-15px' }}>
            <ChartColors iconColor={iconColor} />
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: ChartOptionsStateProps, ownprops: any) => {
  return {
    chartControls: state.chartControls,
    tabTileProps: state.tabTileProps,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    changingValuesofSteps: (propKey: string, value: any) =>
      dispatch(changingValuesofSteps(propKey, value)),
    switchAutotoManualinSteps: (propKey: string, value: any) =>
      dispatch(switchAutotoManualinSteps(propKey, value)),
    addingNewStep: (propKey: string, index: number, value: any) =>
      dispatch(addingNewStep(propKey, index, value)),
    updateGaugeAxisOptions: (propKey: string, option: string, value: any) =>
      dispatch(updateGaugeAxisOptions(propKey, option, value)),
    setManualColorGauge: (propKey: string, value: any) =>
      dispatch(setManualColorGauge(propKey, value)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ColorSteps);
